import { BR_MGMT_BACKEND_URL } from 'services/constants';
import axios from 'axios';
import { BusinessRelationshipLifecycleAndStatus } from 'views/CaseRedirect/CaseRedirect';
import { CONTENT_TYPE_JSON } from 'services/userFeedbackService';
import { BrBusinessEventsType, BrUserAuthoritiesType, CoreDataDto } from 'types/BrTypes';
import { BrLockType } from 'hooks/queries/useFetchBrLock';
import { BrVersionDto } from 'views/RevisionHistory/Br/BrRevisionDtos';
import { RevisionDetail } from 'views/RevisionHistory/RevisionDtos';
import { ClientReadinessDto } from 'hooks/queries/useFetchBrMetaDataWithRelatedPartners';
import { axiosInstance as axiosInstanceWithCredentials } from 'shared/axios/axiosConfig';
import { BlockingInstruction } from 'types/BlockingInstructionTypes';

const axiosInstance = axios.create({ withCredentials: true });

export const jbHeaders = {
  'Content-Type': CONTENT_TYPE_JSON,
  'JB-Auth-Account-Id': '',
  'JB-Servicing-App-Id': 'PCM',
};

export const getBusinessRelationInformation = async (brId?: string): Promise<CoreDataDto> => {
  const json = await axiosInstance.get(
    `${BR_MGMT_BACKEND_URL}/api/internal/v1/business-relationships/core-data/${brId}?withPartnerInfo=false&withPartnerProfileInfo=false`,
  );
  return json.data;
};

export const startBrOnboardingProcess = async (brUid: string) => {
  const { data } = await axios.put(
    `${BR_MGMT_BACKEND_URL}/api/internal/v1/business-relationships/start-onboarding/${brUid}`,
  );
  return data;
};

export const getBrLifecycleAndStatus = async (brUid: string): Promise<BusinessRelationshipLifecycleAndStatus> => {
  const request = axiosInstance.get(
    `${BR_MGMT_BACKEND_URL}/api/internal/v1/business-relationships/${brUid}?withPartnerInfo=false&withPartnerProfileInfo=false}`,
    {},
  );
  const response = await request;
  return {
    businessRelationshipLifecycle: response?.data?.businessRelationshipLifecycle,
    businessRelationshipStatus: response?.data?.businessRelationshipStatus,
  };
};

/**
 * Call the pcm-BR-frontend-controller to list of Business Managers for certain prospectID
 */
export const getBusinessRelations = async (partnerId: string, intermediaryOnly?: boolean): Promise<any[]> => {
  const request = axiosInstance.post(
    `${BR_MGMT_BACKEND_URL}/api/v1/business-relationships/search`,
    JSON.stringify({
      prnUid: partnerId,
      ...(intermediaryOnly && { busRelType: 'INTERMEDIARY_B2B' }),
    }),
    {
      headers: jbHeaders,
    },
  );
  const response = await request;
  return response.data;
};

export const callBrBusinessEvent = async (brUid: string, businessEvent: BrBusinessEventsType): Promise<any> => {
  const request = axiosInstance.put(
    `${BR_MGMT_BACKEND_URL}/api/v1/business-relationships/${brUid}/business-events`,
    JSON.stringify({
      businessEvent: businessEvent,
    }),
    {
      headers: jbHeaders,
    },
  );
  const response = await request;
  return response.data;
};

/**
 * Get authorities of the logged-in user
 */
export const getUserAuthorities = async (): Promise<BrUserAuthoritiesType[]> => {
  const url = `${BR_MGMT_BACKEND_URL}/api/internal/user/user-authorities/read`;
  const response = await axiosInstance.get(url);
  return response.data.authorityList;
};

/**
 * Get br versions
 */
export const getBrVersions = async (brUid: string): Promise<BrVersionDto[]> => {
  const url = `${BR_MGMT_BACKEND_URL}/api/business-relationships/${brUid}/versions`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getBrComparison = async (
  brUid: string,
  oldVersion: number | null,
  newVersion: number,
): Promise<RevisionDetail[]> => {
  const { data } = await axiosInstance.get(`${BR_MGMT_BACKEND_URL}/api/business-relationships/${brUid}/comparison`, {
    params: {
      oldVersion,
      newVersion,
    },
  });

  return data;
};

export const getBrLockStatus = async (brUid?: string): Promise<BrLockType[]> => {
  const url = `${BR_MGMT_BACKEND_URL}/api/business-relationships/${brUid}/lock`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getBlockingInstructions = async (brUid?: string): Promise<BlockingInstruction[]> => {
  const url = `${BR_MGMT_BACKEND_URL}/api/v1/business-relationships/${brUid}/blocking-instructions`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getBrMetaDataWithRelatedPartners = async (brUid?: string): Promise<ClientReadinessDto> => {
  const url = `${BR_MGMT_BACKEND_URL}/api/v1/business-relationships/${brUid}/client-readiness?include-profiling=false`;

  const response = await axiosInstanceWithCredentials.get(url);
  return response.data;
};

const businessRelationService = { getBusinessRelationInformation, getBrVersions, getBrComparison };
export default businessRelationService;
